import { motion } from 'framer-motion'

import { cn } from '@/lib/utils'

export interface BarDatum {
  label: string
  value: number
}

const EASE = [0.16, 1, 0.3, 1] as const

// ── Vertical bars ──────────────────────────────────────────────────────────
const W = 640
const PAD = { top: 30, right: 16, bottom: 36, left: 16 }
const GRID = [0.5, 1]

interface BarsProps {
  data: BarDatum[]
  formatValue?: (n: number) => string
  className?: string
  height?: number
  /** highlight the tallest bar in amber-strong (default true) */
  highlightMax?: boolean
}

/** Hand-rolled vertical bar chart. Bars grow up on mount, hairline baseline. */
export function Bars({
  data,
  formatValue = String,
  className,
  height = 260,
  highlightMax = true,
}: BarsProps) {
  const n = data.length
  if (n === 0) return <Empty className={className} height={height} />

  const innerW = W - PAD.left - PAD.right
  const innerH = height - PAD.top - PAD.bottom
  const baseY = PAD.top + innerH
  const max = Math.max(1, ...data.map((d) => d.value))
  const band = innerW / n
  const bw = Math.min(64, band * 0.56)
  const maxVal = Math.max(...data.map((d) => d.value))

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      className={cn('h-auto w-full overflow-visible', className)}
      role="img"
      aria-label="Grafic cu bare"
    >
      {GRID.map((f) => (
        <line
          key={f}
          x1={PAD.left}
          x2={W - PAD.right}
          y1={baseY - f * innerH}
          y2={baseY - f * innerH}
          stroke="var(--hairline)"
          strokeWidth={1}
          strokeDasharray="2 5"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {data.map((d, i) => {
        const h = (d.value / max) * innerH
        const cx = PAD.left + band * i + band / 2
        const bx = cx - bw / 2
        const top = baseY - h
        const isMax = highlightMax && d.value === maxVal && d.value > 0
        return (
          <g key={`${i}-${d.label}`}>
            <motion.rect
              x={bx}
              width={bw}
              rx={5}
              fill={isMax ? 'var(--amber-strong)' : 'var(--amber)'}
              fillOpacity={isMax ? 1 : 0.82}
              initial={{ height: 0, y: baseY }}
              animate={{ height: Math.max(h, 0.001), y: top }}
              transition={{ duration: 0.7, delay: 0.06 * i, ease: EASE }}
            />
            <motion.text
              x={cx}
              y={top - 9}
              textAnchor="middle"
              className="fill-amber-ink font-mono"
              fontSize={13}
              fontWeight={600}
              initial={{ opacity: 0 }}
              animate={{ opacity: d.value > 0 ? 1 : 0.4 }}
              transition={{ delay: 0.06 * i + 0.4 }}
            >
              {formatValue(d.value)}
            </motion.text>
            <text x={cx} y={height - 12} textAnchor="middle" className="fill-taupe" fontSize={13}>
              {d.label}
            </text>
          </g>
        )
      })}

      <line
        x1={PAD.left}
        x2={W - PAD.right}
        y1={baseY}
        y2={baseY}
        stroke="var(--hairline)"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

// ── Horizontal ranked bars ───────────────────────────────────────────────────
interface HBarsProps {
  data: BarDatum[]
  formatValue?: (n: number) => string
  className?: string
  /** row height in px */
  rowHeight?: number
  /** dim bars further down the ranking (default true) */
  rampByRank?: boolean
}

const HW = 600
const HPAD = 6

/** Hand-rolled ranked meter list: name + value above a full-width bar that grows in. */
export function HBars({
  data,
  formatValue = String,
  className,
  rowHeight = 52,
  rampByRank = true,
}: HBarsProps) {
  const n = data.length
  if (n === 0) return <Empty className={className} height={rowHeight * 3} />

  const max = Math.max(1, ...data.map((d) => d.value))
  const height = n * rowHeight
  const trackW = HW - HPAD * 2

  return (
    <svg
      viewBox={`0 0 ${HW} ${height}`}
      className={cn('h-auto w-full', className)}
      role="img"
      aria-label="Clasament cu bare"
    >
      {data.map((d, i) => {
        const top = i * rowHeight
        const barY = top + rowHeight - 20
        const w = (d.value / max) * trackW
        const opacity = rampByRank ? 1 - (i / Math.max(1, n)) * 0.5 : 1
        return (
          <g key={`${i}-${d.label}`}>
            <text
              x={HPAD}
              y={top + 15}
              className="fill-foreground"
              fontSize={14.5}
              fontWeight={500}
            >
              {d.label}
            </text>
            <text
              x={HW - HPAD}
              y={top + 15}
              textAnchor="end"
              className="fill-amber-ink font-mono"
              fontSize={14}
              fontWeight={600}
            >
              {formatValue(d.value)}
            </text>
            <rect
              x={HPAD}
              y={barY}
              width={trackW}
              height={10}
              rx={5}
              fill="var(--muted)"
              stroke="var(--hairline)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <motion.rect
              y={barY}
              height={10}
              rx={5}
              fill={i === 0 ? 'var(--amber-strong)' : 'var(--amber)'}
              fillOpacity={opacity}
              initial={{ x: HPAD, width: 0 }}
              animate={{ x: HPAD, width: Math.max(w, 0.001) }}
              transition={{ duration: 0.75, delay: 0.07 * i, ease: EASE }}
            />
          </g>
        )
      })}
    </svg>
  )
}

function Empty({ className, height }: { className?: string; height: number }) {
  return (
    <div
      className={cn('flex items-center justify-center text-sm text-muted-foreground', className)}
      style={{ height }}
    >
      Nu există date de afișat.
    </div>
  )
}
